import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { settingsAPI, aiAPI } from '../services/api';
import { FONTS, applyTheme } from '../utils/theme';
import Icon from './Icon';

const DEFAULT_SETTINGS = {
  general: {
    platformName: 'Recruiters',
    logoUrl: '/assets/logo.png',
    supportEmail: 'eaprecruiter@ouk.ac.ke',
    maintenanceMode: false
  },
  theme: {
    primaryColor: '#037b90',
    secondaryColor: '#ff7f50',
    font: 'Poppins',
    borderRadius: 8
  },
  email: {
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromName: 'OUK STUDENT RECRUITERS',
    fromEmail: 'eaprecruiter@ouk.ac.ke'
  },
  ai: {
    enabled: false,
    openRouterApiKey: '',
    model: 'openai/gpt-4o-mini',
    allowAgentActions: false
  }
};

const AI_MODELS = [
  'openai/gpt-4o-mini',
  'openai/gpt-4o',
  'anthropic/claude-3.5-sonnet',
  'anthropic/claude-3-haiku',
  'meta-llama/llama-3.1-8b-instruct',
  'google/gemini-flash-1.5'
];

const labelStyle = {
  fontSize: '14px',
  fontWeight: '600',
  marginBottom: '8px',
  display: 'block'
};

function SettingsManagement() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [aiKey, setAiKey] = useState('');
  const [keySet, setKeySet] = useState(false);
  const [testingAI, setTestingAI] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  // Live preview: apply theme changes app-wide as the admin edits.
  useEffect(() => {
    applyTheme(settings);
  }, [settings.theme]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.get();
      setSettings({ ...DEFAULT_SETTINGS, ...res.data });
      if (!testEmail) setTestEmail(res.data.email?.fromEmail || '');
      const ai = res.data.ai || {};
      setKeySet(!!ai.keyConfigured);
      setAiKey(ai.openRouterApiKey && ai.openRouterApiKey !== '********' ? ai.openRouterApiKey : '');
    } catch (e) {
      toast.error(e.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const update = (group, key, value) => {
    setSettings((prev) => ({
      ...prev,
      [group]: { ...prev[group], [key]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        ai: {
          ...settings.ai,
          openRouterApiKey: aiKey !== '' ? aiKey : (keySet ? '********' : '')
        }
      };
      const res = await settingsAPI.update(payload);
      setSettings(res.data.settings);
      applyTheme(res.data.settings);
      const ai = res.data.settings.ai || {};
      setKeySet(!!ai.keyConfigured);
      setAiKey(ai.openRouterApiKey && ai.openRouterApiKey !== '********' ? ai.openRouterApiKey : '');
      toast.success('Settings saved successfully');
    } catch (e) {
      toast.error(e.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestAI = async () => {
    setTestingAI(true);
    try {
      const res = await aiAPI.testKey();
      toast.success(res.data?.ok ? `OpenRouter OK (${res.data.model})` : 'Key test failed');
    } catch (e) {
      toast.error(e.message || 'Key test failed');
    } finally {
      setTestingAI(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Enter a recipient email for the test');
      return;
    }
    setTesting(true);
    try {
      const res = await settingsAPI.testEmail(testEmail);
      toast.success(res.data?.message || `Test email sent to ${testEmail}`);
    } catch (e) {
      toast.error(e.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="spinner" />
          </div>
          <p>Loading settings…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Platform Settings</h1>
          <p className="page-subtitle">Manage the platform branding, appearance, and email configuration.</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Icon name="save" /> {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Appearance */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Appearance</h2>
            <p className="card-subtitle">Theme colors, typography, and shape</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Primary Color</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="color"
                className="form-input"
                style={{ width: '48px', height: '40px', padding: '2px' }}
                value={settings.theme.primaryColor}
                onChange={(e) => update('theme', 'primaryColor', e.target.value)}
              />
              <input
                className="form-input"
                style={{ textTransform: 'uppercase' }}
                value={settings.theme.primaryColor}
                onChange={(e) => update('theme', 'primaryColor', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Secondary Color</label>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="color"
                className="form-input"
                style={{ width: '48px', height: '40px', padding: '2px' }}
                value={settings.theme.secondaryColor}
                onChange={(e) => update('theme', 'secondaryColor', e.target.value)}
              />
              <input
                className="form-input"
                style={{ textTransform: 'uppercase' }}
                value={settings.theme.secondaryColor}
                onChange={(e) => update('theme', 'secondaryColor', e.target.value)}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Font Family</label>
            <select
              className="form-select"
              value={settings.theme.font}
              onChange={(e) => update('theme', 'font', e.target.value)}
            >
              {FONTS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Border Radius (px)</label>
            <input
              type="number"
              className="form-input"
              value={settings.theme.borderRadius}
              onChange={(e) => update('theme', 'borderRadius', parseInt(e.target.value, 10) || 0)}
            />
          </div>
        </div>

        <div style={{ marginTop: '24px', padding: '20px', border: '1px dashed var(--border-color)', borderRadius: 'var(--radius-lg)' }}>
          <span style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary)', display: 'block', marginBottom: '12px' }}>
            Live Preview
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="button">Primary Button</button>
            <a href="#" onClick={(e) => e.preventDefault()} style={{ color: 'var(--primary-color)', fontWeight: 600 }}>
              Link color
            </a>
            <span style={{ fontFamily: `'${settings.theme.font}', sans-serif`, fontSize: '16px', color: 'var(--text-primary)' }}>
              The quick brown fox — {settings.theme.font}
            </span>
          </div>
        </div>
      </div>

      {/* Email & SMTP */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Email &amp; SMTP</h2>
            <p className="card-subtitle">Outgoing email server configuration</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}>SMTP Host</label>
            <input
              className="form-input"
              placeholder="smtp.gmail.com"
              value={settings.email.host}
              onChange={(e) => update('email', 'host', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>SMTP Port</label>
            <input
              type="number"
              className="form-input"
              value={settings.email.port}
              onChange={(e) => update('email', 'port', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div>
            <label style={labelStyle}>SMTP Username</label>
            <input
              className="form-input"
              value={settings.email.user}
              onChange={(e) => update('email', 'user', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>SMTP Password / App Password</label>
            <input
              type="password"
              className="form-input"
              autoComplete="new-password"
              value={settings.email.password}
              onChange={(e) => update('email', 'password', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>From Name</label>
            <input
              className="form-input"
              value={settings.email.fromName}
              onChange={(e) => update('email', 'fromName', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>From Email</label>
            <input
              type="email"
              className="form-input"
              value={settings.email.fromEmail}
              onChange={(e) => update('email', 'fromEmail', e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={!!settings.email.secure}
              onChange={(e) => update('email', 'secure', e.target.checked)}
            />
            Use TLS / Secure (port 465)
          </label>
        </div>
      </div>

      {/* Test Email */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Send Test Email</h2>
            <p className="card-subtitle">Verify the SMTP configuration works</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1', minWidth: '240px' }}>
            <label style={labelStyle}>Recipient Email</label>
            <input
              type="email"
              className="form-input"
              placeholder="admin@ouk.ac.ke"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" onClick={handleTestEmail} disabled={testing}>
            <Icon name="mail" /> {testing ? 'Sending…' : 'Send Test Email'}
          </button>
        </div>
      </div>

      {/* AI / OpenRouter */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">AI / OpenRouter</h2>
            <p className="card-subtitle">Enable the AI assistant and agent tools</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}>OpenRouter API Key</label>
            <input
              type="password"
              className="form-input"
              autoComplete="new-password"
              placeholder={keySet ? 'Key is set — enter a new key to change' : 'sk-or-...'}
              value={aiKey}
              onChange={(e) => setAiKey(e.target.value)}
            />
            {keySet && (
              <p style={{ fontSize: '12px', color: 'var(--accent-green)', marginTop: '6px' }}>
                ✓ API key configured
              </p>
            )}
          </div>
          <div>
            <label style={labelStyle}>Model</label>
            <select
              className="form-select"
              value={settings.ai.model}
              onChange={(e) => update('ai', 'model', e.target.value)}
            >
              {AI_MODELS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <label style={{...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={!!settings.ai.enabled}
              onChange={(e) => update('ai', 'enabled', e.target.checked)}
            />
            Enable AI Assistant
          </label>
          <label style={{...labelStyle, display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={!!settings.ai.allowAgentActions}
              onChange={(e) => update('ai', 'allowAgentActions', e.target.checked)}
            />
            Allow agent to perform actions (e.g., create notifications)
          </label>
        </div>
        <div style={{ marginTop: '16px' }}>
          <button className="btn btn-outline" onClick={handleTestAI} disabled={testingAI}>
            <Icon name="help" /> {testingAI ? 'Testing…' : 'Test Connection'}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
            The key is stored server-side only and never exposed to the browser. Using the assistant consumes OpenRouter credits.
          </p>
        </div>
      </div>

      {/* General */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">General</h2>
            <p className="card-subtitle">Platform name and contacts</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Platform Name</label>
            <input
              className="form-input"
              value={settings.general.platformName}
              onChange={(e) => update('general', 'platformName', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Logo URL</label>
            <input
              className="form-input"
              value={settings.general.logoUrl}
              onChange={(e) => update('general', 'logoUrl', e.target.value)}
            />
          </div>
          <div>
            <label style={labelStyle}>Support Email</label>
            <input
              type="email"
              className="form-input"
              value={settings.general.supportEmail}
              onChange={(e) => update('general', 'supportEmail', e.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={!!settings.general.maintenanceMode}
              onChange={(e) => update('general', 'maintenanceMode', e.target.checked)}
            />
            Maintenance Mode
          </label>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
            When enabled, non-admin users see a maintenance banner.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SettingsManagement;
